// Firebase 라이브러리 임포트
import { initializeApp } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import { getDatabase } from 'firebase/database';
import { getStorage } from 'firebase/storage';
import { getMessaging, getToken, onMessage } from 'firebase/messaging';
import { showToast } from './ui-manager.js';

// Firebase 구성
// 보안을 위해 환경 변수 사용 또는 서버에서 가져오는 방식으로 변경
// 개발 환경에서는 아래 구성을 사용하고, 프로덕션 환경에서는 별도의 서버 엔드포인트에서 가져오는 것이 좋습니다.
const getFirebaseConfig = () => {
  // 브라우저에서는 process.env가 없으므로 window.ENV 또는 기본 설정을 사용
  // 이는 빌드 과정에서 환경 변수를 포함시키는 방식으로 개선되어야 합니다.
  if (window.ENV && window.ENV.FIREBASE_CONFIG) {
    // 빌드 도구가 환경 변수를 window.ENV에 삽입했을 경우
    return window.ENV.FIREBASE_CONFIG;
  }
  
  // 프로덕션 환경인지 확인 (도메인 기반 판단)
  const isProduction = window.location.hostname !== 'localhost' && 
                       window.location.hostname !== '127.0.0.1';
  
  // 개발 환경에서는 로컬 구성 사용
  // 프로덕션에서는 서버에서 설정을 가져오거나 빌드 시 삽입된 설정 사용이 이상적
  // 지금은 개발 설정을 사용하고 보안 경고만 표시
  if (isProduction) {
    console.warn('프로덕션 환경에서 개발용 Firebase 설정을 사용 중입니다. 보안을 위해 서버에서 설정을 가져오는 방식으로 개선해야 합니다.');
  }
  
  // 기본 개발 환경 설정
  return {
    apiKey: "AIzaSyBOwJl_j6s4SFsA9o4io6Ylu17sWLYWkdk",
    authDomain: "kdindoor-1ac7f.firebaseapp.com",
    databaseURL: "https://kdindoor-1ac7f-default-rtdb.asia-southeast1.firebasedatabase.app",
    projectId: "kdindoor-1ac7f",
    storageBucket: "kdindoor-1ac7f.firebasestorage.app",
    messagingSenderId: "117044859093",
    appId: "1:117044859093:web:21873c0dcb77df22ae525c"
  };
};

// Firebase 초기화
const firebaseConfig = getFirebaseConfig();
const firebaseApp = initializeApp(firebaseConfig);
const auth = getAuth(firebaseApp);
const database = getDatabase(firebaseApp);
const storage = getStorage(firebaseApp);

// Firebase Cloud Messaging 초기화
let messaging = null;
let messagingInitialized = false;

// FCM 초기화 함수
const initializeMessaging = async () => {
  if (messagingInitialized) return messaging;
  
  try {
    // 브라우저가 FCM을 지원하는지 확인
    if (typeof navigator !== 'undefined' && 'serviceWorker' in navigator) {
      const isSupported = await import('firebase/messaging').then(module => module.isSupported());
      
      if (isSupported) {
        messaging = getMessaging(firebaseApp);
        messagingInitialized = true;
        console.log('Firebase Cloud Messaging이 초기화되었습니다.');
        
        // 알림 리스너 설정
        setupNotificationListener((data) => {
          console.log('알림 클릭 이벤트 데이터:', data);
          
          // 채팅방 ID가 있으면 자동으로 채팅방 열기
          if (data && data.chatRoomId) {
            // 현재 앱의 구현에 맞게 채팅방 열기 함수 호출
            try {
              const openChatRoomFromNotification = window.openChatRoomFromNotification;
              if (typeof openChatRoomFromNotification === 'function') {
                openChatRoomFromNotification(data.chatRoomId);
              }
            } catch (error) {
              console.error('채팅방 열기 함수 호출 오류:', error);
            }
          }
        });
      } else {
        console.warn('이 브라우저는 Firebase Cloud Messaging을 지원하지 않습니다.');
      }
    } else {
      console.warn('이 브라우저는 serviceWorker를 지원하지 않습니다.');
    }
  } catch (error) {
    console.error('Firebase Cloud Messaging 초기화 오류:', error);
  }
  
  return messaging;
};

// 앱 시작 시 FCM 초기화 시도
initializeMessaging();

// FCM 토큰 요청 함수
export const requestNotificationPermission = async () => {
  // 메시징이 초기화되지 않았다면 초기화 시도
  if (!messaging) {
    messaging = await initializeMessaging();
  }
  
  if (!messaging) {
    console.warn('Firebase 메시징이 지원되지 않습니다.');
    return null;
  }
  
  try {
    // 알림 권한 요청
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      console.warn('알림 권한이 거부되었습니다.');
      return null;
    }
    
    // 서비스 워커 등록 확인
    let swRegistration;
    try {
      // 먼저 서비스 워커가 이미 등록되어 있는지 확인
      const registrations = await navigator.serviceWorker.getRegistrations();
      const existingRegistration = registrations.find(reg => 
        reg.scope.includes('/') && reg.active?.scriptURL.includes('firebase-messaging-sw.js')
      );
      
      if (existingRegistration) {
        console.log('기존 서비스 워커를 사용합니다:', existingRegistration);
        swRegistration = existingRegistration;
      } else {
        // 서비스 워커 등록 (없는 경우)
        swRegistration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
          scope: '/'
        });
        console.log('서비스 워커가 등록되었습니다.', swRegistration);
        
        // 서비스 워커가 활성화될 때까지 대기
        if (swRegistration.installing) {
          console.log('서비스 워커 설치 중...');
          const sw = swRegistration.installing || swRegistration.waiting;
          await new Promise(resolve => {
            sw.addEventListener('statechange', e => {
              if (e.target.state === 'activated') {
                console.log('서비스 워커가 활성화되었습니다!');
                resolve();
              }
            });
          });
        }
      }
    } catch (error) {
      console.error('서비스 워커 등록 오류:', error);
      return null;
    }
    
    // 현재 사용 중인 vapidKey
    const vapidKey = 'BBDyHvBi6XWfgcOKxtNiRx9ZgD8Ocy6gzwk-C-XOzsRaa53kMQaJWFRQPPU3W-u--tzT0njMW4wKAM4jX1XCMmE';
    
    // FCM 토큰 생성
    const token = await getToken(messaging, {
      vapidKey: vapidKey,
      serviceWorkerRegistration: swRegistration
    });
    
    if (token) {
      console.log('FCM 토큰:', token);
      return token;
    } else {
      console.warn('FCM 토큰을 가져올 수 없습니다.');
      return null;
    }
  } catch (error) {
    console.error('FCM 토큰 요청 오류:', error);
    return null;
  }
};

// 알림 메시지 수신 처리
export const setupNotificationListener = (callback) => {
  if (!messaging) return;
  
  onMessage(messaging, (payload) => {
    console.log('새 알림 메시지:', payload);
    
    // 알림 데이터 추출
    const notificationTitle = payload.notification?.title || '새 메시지';
    const notificationBody = payload.notification?.body || '새로운 채팅 메시지가 있습니다.';
    
    // 본문 텍스트 길이 제한 (두 줄 정도로 보이게)
    const shortBody = notificationBody.length > 40 ? notificationBody.substring(0, 40) + '...' : notificationBody;
    
    // FCM 알림용 간략화된 토스트 표시 (상단에 표시)
    showFcmToast(notificationTitle, shortBody, 10000);
    
    // 브라우저 알림 표시 (선택적으로 사용 가능)
    if (Notification.permission === 'granted') {
      const notificationOptions = {
        body: notificationBody,
        icon: '/assets/icons/icon-192x192.png',
        badge: '/assets/icons/badge-96x96.png',
        data: payload.data,
        tag: 'chat-message',
        renotify: true
      };
      
      // 브라우저 알림 표시
      const notification = new Notification(notificationTitle, notificationOptions);
      
      // 알림 클릭 이벤트
      notification.onclick = () => {
        notification.close();
        window.focus();
        
        // 콜백 함수 호출
        if (typeof callback === 'function') {
          callback(payload.data);
        }
      };
    }
  });
};

/**
 * FCM 알림용 간소화된 토스트 메시지를 상단에 표시
 * @param {string} title - 알림 제목
 * @param {string} body - 알림 내용
 * @param {number} duration - 표시 지속 시간 (ms)
 */
export function showFcmToast(title, body, duration = 10000) {
  // 이미 존재하는 FCM 토스트 컨테이너를 찾거나 새로 생성
  let toastContainer = document.querySelector('.fcm-toast-container');
  
  if (!toastContainer) {
    toastContainer = document.createElement('div');
    toastContainer.className = 'fcm-toast-container position-fixed top-0 end-0 p-1';
    toastContainer.style.zIndex = '1070'; // Bootstrap 모달보다 위에 표시
    document.body.appendChild(toastContainer);
  }
  
  // 고유 ID 생성
  const toastId = `fcm-toast-${Date.now()}`;
  
  // 간소화된 토스트 엘리먼트 생성
  const toastHtml = `
    <div id="${toastId}" class="toast show" role="alert" aria-live="assertive" aria-atomic="true">
      <div class="toast-header bg-primary text-white py-2">
        <i class="bi bi-bell me-2"></i>
        <strong class="me-auto">${title}</strong>
        <button type="button" class="btn-close btn-close-white" onclick="document.getElementById('${toastId}').remove()"></button>
      </div>
      <div class="toast-body py-2">
        ${body}
      </div>
    </div>
  `;
  
  // 토스트 추가
  toastContainer.insertAdjacentHTML('beforeend', toastHtml);
  
  // 토스트 엘리먼트
  const toastElement = document.getElementById(toastId);
  
  // 스타일 추가 - 폭 제한 및 그림자 효과
  toastElement.style.maxWidth = '350px';
  toastElement.style.boxShadow = '0 0.5rem 1rem rgba(0, 0, 0, 0.15)';
  
  // 지정된 시간 후 DOM에서 제거
  setTimeout(() => {
    if (toastElement && toastElement.parentNode) {
      // 페이드 아웃 효과
      toastElement.classList.add('fade');
      
      // 완전히 제거
      setTimeout(() => {
        if (toastElement.parentNode) {
          toastElement.parentNode.removeChild(toastElement);
        }
        
        // 컨테이너에 더 이상 토스트가 없으면 컨테이너도 제거
        if (toastContainer && toastContainer.children.length === 0) {
          toastContainer.remove();
        }
      }, 300);
    }
  }, duration);
}

export { firebaseApp, auth, database, storage, messaging }; 